import { ChangeEvent, Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { ROUTES } from '../../helpers/constans';
import { EUserPermission } from '../../types/@user';
import { IAdminEventDetail, IAdminEventListItem } from '../../types/@event';
import { fetchAdminEventById, fetchAdminEventRiveUpload, fetchAdminEvents } from '../../services/event.service';

const boxStyles = {
  container: { p: 3 },
  titleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' },
  detailGrid: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 },
  detailCard: { p: 2, borderRadius: 2 },
  uploadCard: { p: 2, borderRadius: 2, mt: 2 },
};

const SystemEventsRiveManager = (): ReactElement => {
  const auth = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const { id } = useParams();
  const [events, setEvents] = useState<IAdminEventListItem[]>([]);
  const [pages, setPages] = useState<number>(1);
  const [page, setPage] = useState<number>(1);
  const [loadingList, setLoadingList] = useState<boolean>(false);
  const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [detail, setDetail] = useState<IAdminEventDetail | null>(null);
  const [portraitFile, setPortraitFile] = useState<File | null>(null);
  const [landscapeFile, setLandscapeFile] = useState<File | null>(null);

  const isAdmin = Number(auth?.permission || 0) >= EUserPermission.ADMIN;

  const selectedTitle = useMemo(() => {
    if (!detail) return 'Event Rive Manager';
    return `${detail.name || 'Event'} Rive Manager`;
  }, [detail]);

  const loadList = async (nextPage: number) => {
    setLoadingList(true);
    setError('');
    try {
      const response = await fetchAdminEvents(nextPage);
      setEvents(response.data?.data || []);
      setPages(response.data?.pages || 1);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Unable to load events');
    } finally {
      setLoadingList(false);
    }
  };

  const loadDetail = async (eventId: string) => {
    setLoadingDetail(true);
    setError('');
    try {
      const response = await fetchAdminEventById(eventId);
      setDetail(response.data || null);
      setPortraitFile(null);
      setLandscapeFile(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Unable to load event details');
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadList(page);
  }, [isAdmin, page]);

  useEffect(() => {
    if (id && isAdmin) {
      loadDetail(id);
    } else {
      setDetail(null);
    }
  }, [id, isAdmin]);

  const handlePagination = (_: ChangeEvent<unknown>, nextPage: number) => {
    setPage(nextPage);
  };

  const handleOpen = (eventId: string) => {
    navigate(`/system/events-rive/${eventId}`);
  };

  const handleBack = () => {
    navigate(ROUTES.systemEventsRive);
  };

  const handleUpload = async () => {
    if (!detail?._id) return;
    if (!portraitFile && !landscapeFile) {
      setError('Select at least one .riv file to upload');
      return;
    }

    const formData = new FormData();
    if (portraitFile) formData.append('portrait', portraitFile);
    if (landscapeFile) formData.append('landscape', landscapeFile);

    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetchAdminEventRiveUpload(detail._id, formData);
      setDetail(response.data || null);
      setSuccess('Rive assets updated for both Mintfinity and QDOS sync');
      setPortraitFile(null);
      setLandscapeFile(null);
      await loadList(page);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Unable to upload rive files');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={boxStyles.container}>
        <Alert severity="error">System admin access is required for event Rive management.</Alert>
      </Box>
    );
  }

  return (
    <Fragment>
      <Box sx={boxStyles.container}>
        <Box sx={boxStyles.titleRow}>
          <Box>
            <Typography variant="h4">{selectedTitle}</Typography>
            <Typography variant="body1">Manage portrait and landscape .riv files stored on S3 and synced into the linked QDOS event.</Typography>
          </Box>
          {detail ? (
            <Button variant="outlined" onClick={handleBack}>Back to list</Button>
          ) : null}
        </Box>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}

        {!detail ? (
          <Paper sx={boxStyles.detailCard}>
            {loadingList ? (
              <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={20} /><Typography>Loading events…</Typography></Stack>
            ) : (
              <Fragment>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Organisation</TableCell>
                        <TableCell>Team</TableCell>
                        <TableCell>QDOS event</TableCell>
                        <TableCell>Portrait riv</TableCell>
                        <TableCell>Landscape riv</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {events.map((event) => (
                        <TableRow hover key={event._id}>
                          <TableCell>{event.name}</TableCell>
                          <TableCell>{event.organisationName || '—'}</TableCell>
                          <TableCell>{event.teamName || '—'}</TableCell>
                          <TableCell>{event.qdosEventId || '—'}</TableCell>
                          <TableCell>{event.rivePortrait?.originalname || '—'}</TableCell>
                          <TableCell>{event.riveLandscape?.originalname || '—'}</TableCell>
                          <TableCell>{event.updatedAt ? new Date(event.updatedAt).toLocaleString() : '—'}</TableCell>
                          <TableCell>
                            <Button variant="contained" onClick={() => handleOpen(event._id)}>Manage</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Pagination count={pages} page={page} onChange={handlePagination} />
                </Box>
              </Fragment>
            )}
          </Paper>
        ) : (
          <Fragment>
            {loadingDetail ? (
              <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={20} /><Typography>Loading event details…</Typography></Stack>
            ) : (
              <Fragment>
                <Box sx={boxStyles.detailGrid}>
                  <Paper sx={boxStyles.detailCard}>
                    <Typography variant="h6">Event details</Typography>
                    <Typography>Name: {detail.name || '—'}</Typography>
                    <Typography>QDOS event ID: {detail.qdosEventId || '—'}</Typography>
                    <Typography>Organisation: {detail.organisation?.name || '—'}</Typography>
                    <Typography>Team: {detail.team?.name || '—'}</Typography>
                    <Typography>Source: {detail.source || '—'}</Typography>
                  </Paper>
                  <Paper sx={boxStyles.detailCard}>
                    <Typography variant="h6">Current rive files</Typography>
                    <Typography>Portrait: {detail.rivePortrait?.originalname || '—'}</Typography>
                    {detail.rivePortrait?.location ? <Link href={detail.rivePortrait.location} target="_blank" rel="noreferrer">Open portrait file</Link> : null}
                    <Box sx={{ mt: 2 }} />
                    <Typography>Landscape: {detail.riveLandscape?.originalname || '—'}</Typography>
                    {detail.riveLandscape?.location ? <Link href={detail.riveLandscape.location} target="_blank" rel="noreferrer">Open landscape file</Link> : null}
                  </Paper>
                </Box>

                <Paper sx={boxStyles.uploadCard}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Upload replacement .riv files</Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>Portrait .riv</Typography>
                      <input type="file" accept=".riv" onChange={(event) => setPortraitFile(event.target.files?.[0] || null)} />
                      {portraitFile ? <Typography variant="caption" display="block">Selected: {portraitFile.name}</Typography> : null}
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>Landscape .riv</Typography>
                      <input type="file" accept=".riv" onChange={(event) => setLandscapeFile(event.target.files?.[0] || null)} />
                      {landscapeFile ? <Typography variant="caption" display="block">Selected: {landscapeFile.name}</Typography> : null}
                    </Box>
                    <Stack direction="row" spacing={2}>
                      <Button variant="contained" disabled={submitting} onClick={handleUpload}>Upload rive files</Button>
                      <Button variant="outlined" disabled={submitting} onClick={() => { setPortraitFile(null); setLandscapeFile(null); }}>Clear selection</Button>
                    </Stack>
                  </Stack>
                </Paper>
              </Fragment>
            )}
          </Fragment>
        )}
      </Box>
    </Fragment>
  );
};

export default SystemEventsRiveManager;
